/**
 * Spec 200 — WorkList route. Read-only paridad with mobile minus the
 * write actions (no archive, no FAB → NewWork, no swipe-to-delete).
 *
 * Stack:
 *   - reads from IndexedDB via `db.ts#getWorks()` (Fase 2)
 *   - search debounced 300 ms (matches mobile behaviour)
 *   - status filter chips: all / pending / in_progress / completed
 *   - sort selector: recent / oldest / az / updated
 *   - tap card → navigate `/works/:id` (WorkDetail in the next bloque)
 *
 * State strategy: useState only. The mobile screen also avoids a
 * state library (cf. spec 200 §4.1: "sin lib de estado"). Filters
 * + sort live in URL search params so a refresh keeps the user's
 * selection — useful for desktop muscle memory ("cmd-R, my
 * filters survive").
 */
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { ChevronRight, Pin, Plus, Search, Settings as SettingsIcon, X } from 'lucide-react';

import { BrandMark } from '../components/BrandMark';
import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';
import { StatusBadge } from '../components/ui/StatusBadge';
import { PriorityBadge } from '../components/ui/PriorityBadge';
import { getWorks, subscribeStore } from '../store/db';
import type { WebIdentity } from '../store/keys';
import { getPinnedWorks, subscribePinnedWorks, togglePinnedWork } from '../store/pinnedWorks';
import { useWorkers } from '../sync/useWorkers';
import type { Work } from '../types';

import './WorkList.css';

type SortKey = 'recent' | 'oldest' | 'az' | 'updated';
type StatusFilter = 'all' | Work['status'];

const SORT_OPTIONS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: 'recent', label: 'Recientes' },
  { key: 'oldest', label: 'Antiguos' },
  { key: 'az', label: 'A–Z' },
  { key: 'updated', label: 'Actualizados' },
];

const STATUS_OPTIONS: ReadonlyArray<{ key: StatusFilter; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'pending', label: 'Pendiente' },
  { key: 'in_progress', label: 'En curso' },
  { key: 'completed', label: 'Completado' },
];

const STATUS_LABEL: Record<Work['status'], string> = {
  pending: 'Pendiente',
  in_progress: 'En curso',
  completed: 'Completado',
};

interface WorkListProps {
  identity: WebIdentity;
}

/** Short relative-time formatter for the secondary card row.
 *  Shapes: "ahora" (<60 s), "hace N min" (<60 min), "hace N h" (<24 h),
 *  "hace N d" (<30 d), absolute date thereafter. Lighter weight than
 *  `Clients.tsx#formatRelative` (which leans on "Hoy"/"Ayer"/"Hace N sem")
 *  because card metadata wants a tight, glanceable token, not prose. */
function formatRelative(ms: number): string {
  const delta = Date.now() - ms;
  if (delta < 60_000) return 'ahora';
  const min = Math.floor(delta / 60_000);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `hace ${d} d`;
  return new Date(ms).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
  });
}

/** First-letter pair for an avatar. Mirrors `TechnicianMultiSelect#initials`
 *  but kept inline so this route doesn't grow a util import for one helper. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function compareWorks(a: Work, b: Work, key: SortKey): number {
  switch (key) {
    case 'recent':
      return b.createdAt - a.createdAt;
    case 'oldest':
      return a.createdAt - b.createdAt;
    case 'az':
      return a.title.localeCompare(b.title, 'es', { sensitivity: 'base' });
    case 'updated':
      return b.updatedAt - a.updatedAt;
  }
}

export function WorkList({ identity }: WorkListProps) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // Roster from the relay — used to map technicianId → display name on
  // each card's avatar row. If a worker hasn't surfaced yet (or the
  // relay is offline) the fallback below renders "?" instead of their
  // initials so the avatar still reserves space.
  const workers = useWorkers();
  const workerById = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of workers) m.set(w.id, w.name);
    return m;
  }, [workers]);

  const [works, setWorks] = useState<Work[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(params.get('q') ?? '');
  const status = (params.get('status') ?? 'all') as StatusFilter;
  const sort = (params.get('sort') ?? 'recent') as SortKey;

  // WhatsApp-style pinned-on-top: pinned ids float to the head of
  // the rendered list. `useSyncExternalStore` re-renders synchronously
  // on toggle without the useState+useEffect dance.
  const pinnedSet = useSyncExternalStore(
    subscribePinnedWorks,
    getPinnedWorks,
    getPinnedWorks,
  );

  // Debounce search → 300 ms, matches mobile.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  // Persist debounced search in URL so a refresh keeps it.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedSearch) next.set('q', debouncedSearch);
    else next.delete('q');
    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true });
    }
  }, [debouncedSearch, params, setParams]);

  // Initial load + cache rehydration. Also subscribes to store
  // mutations so a live-sync arrival from the relay re-renders
  // without a manual refresh.
  useEffect(() => {
    let cancelled = false;
    async function reload() {
      try {
        const list = await getWorks();
        if (!cancelled) setWorks(list);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Error al cargar');
      }
    }
    reload();
    const off = subscribeStore(() => {
      void reload();
    });
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  function setStatus(next: StatusFilter) {
    const params2 = new URLSearchParams(params);
    if (next === 'all') params2.delete('status');
    else params2.set('status', next);
    setParams(params2, { replace: true });
  }
  function setSort(next: SortKey) {
    const params2 = new URLSearchParams(params);
    if (next === 'recent') params2.delete('sort');
    else params2.set('sort', next);
    setParams(params2, { replace: true });
  }

  const filtered = useMemo<Work[]>(() => {
    if (!works) return [];
    const q = debouncedSearch.trim().toLowerCase();
    let out = works.slice();
    if (status !== 'all') out = out.filter((w) => w.status === status);
    if (q) {
      out = out.filter((w) => {
        // Searchable surface: title, description, client name +
        // address + tax id, internal notes. Mirrors mobile's
        // search semantics.
        const hay = [w.title, w.description, w.clientName, w.clientAddress, w.clientTaxId, w.notes]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    out.sort((a, b) => compareWorks(a, b, sort));
    // Stable second pass — JS Array.sort is stable, so equal entries
    // (both pinned or both unpinned) keep the order from compareWorks.
    // Pinned floats above unpinned without disturbing sibling order.
    out = [...out].sort((a, b) => Number(pinnedSet.has(b.id)) - Number(pinnedSet.has(a.id)));
    return out;
  }, [works, debouncedSearch, status, sort, pinnedSet]);

  // Per-status counters surfaced inside the filter chips. Computed
  // from the search-scoped slice (NOT status-scoped) so toggling a
  // chip never changes the numbers shown on the other chips —
  // otherwise a user clicking "Pendiente" would see "(0)" next to
  // "En curso" and assume there are none, which is misleading.
  const counts = useMemo(() => {
    const acc = { all: 0, pending: 0, in_progress: 0, completed: 0 };
    if (!works) return acc;
    const q = debouncedSearch.trim().toLowerCase();
    for (const w of works) {
      if (q) {
        const hay = [w.title, w.description, w.clientName, w.clientAddress, w.clientTaxId, w.notes]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) continue;
      }
      acc.all += 1;
      acc[w.status] += 1;
    }
    return acc;
  }, [works, debouncedSearch]);

  return (
    <div className="wl-shell" data-testid="worklist-screen">
      <header className="wl-header">
        <div className="wl-header-inner">
          <div className="wl-brand-block">
            <BrandMark size={28} ariaLabel="Cuadrilla" />
            <span className="wl-brand">Cuadrilla</span>
          </div>
          <span className="wl-subbrand" data-testid="worklist-name">
            {identity.name}
          </span>
          <button
            type="button"
            className="wl-settings-btn"
            onClick={() => navigate('/settings')}
            data-testid="worklist-settings"
            aria-label="Abrir ajustes"
          >
            <SettingsIcon size={20} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </header>

      <main id="main-content" className="wl-main" tabIndex={-1}>
        {/* axe-core requires every page to expose at least one h1.
            The design uses the brand mark in the header instead of
            an explicit page title, so we render a screen-reader-only
            h1 that names the route ("Trabajos") for assistive tech. */}
        <h1 className="sr-only">Trabajos</h1>
        <div className="wl-search-row">
          <Search size={20} strokeWidth={2} aria-hidden="true" className="wl-search-icon" />
          <input
            data-testid="worklist-search"
            className="wl-search"
            placeholder="Buscar trabajos, clientes, direcciones…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              data-testid="worklist-search-clear"
              className="wl-search-clear"
              onClick={() => setSearch('')}
              type="button"
              aria-label="Borrar búsqueda"
            >
              <X size={16} strokeWidth={2} />
            </button>
          )}
        </div>

        <ToggleGroup.Root
          className="wl-filters"
          type="single"
          value={status}
          onValueChange={(v) => {
            // ToggleGroup emits an empty string on toggle-off in
            // single mode; the spec wants the user to ALWAYS land
            // on a filter, so a deselect collapses to "all".
            setStatus((v as StatusFilter) || 'all');
          }}
          aria-label="Filtros de estado"
        >
          {STATUS_OPTIONS.map((opt) => (
            <ToggleGroup.Item
              key={opt.key}
              value={opt.key}
              className="wl-chip"
              data-testid={`worklist-status-${opt.key}`}
            >
              {opt.key === 'all' ? opt.label : `${opt.label} · ${counts[opt.key]}`}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>

        <div className="wl-sort-row">
          <span className="wl-sort-label">Ordenar:</span>
          <div
            className="wl-sort-segmented"
            role="group"
            aria-label="Ordenar"
            data-testid="worklist-sort"
          >
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`wl-sort-segment${sort === opt.key ? ' wl-sort-segment--active' : ''}`}
                aria-pressed={sort === opt.key}
                onClick={() => setSort(opt.key)}
                data-testid={`worklist-sort-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loadError ? (
          <div className="wl-error" data-testid="worklist-error">
            <p>{loadError}</p>
          </div>
        ) : works === null ? (
          /* spec 216 PR1 — skeleton list replaces the centered
             "Cargando…" paragraph. Six rows roughly fill the viewport
             above the fold on a 1080p laptop, matching what the
             populated list shows. The wrapper keeps the `worklist-
             loading` test id so existing assertions still resolve. */
          <SkeletonList count={6} testId="worklist-loading" />
        ) : filtered.length === 0 ? (
          works.length === 0 ? (
            <EmptyState
              testId="worklist-empty"
              variant="noWorks"
              title="Aún no hay trabajos"
              body={
                <>
                  Crea uno con <kbd>N</kbd> o desde el móvil. Aparecerán aquí
                  según los recibas o los crees.
                </>
              }
              action={{
                label: 'Crear trabajo',
                onClick: () => navigate('/works/new'),
                testId: 'worklist-empty-create',
              }}
            />
          ) : (
            <EmptyState
              testId="worklist-empty"
              variant="noResults"
              title="Sin resultados"
              body={
                <>
                  Ningún trabajo coincide con tu búsqueda. Prueba con otro
                  término o limpia los filtros.
                </>
              }
              action={{
                label: 'Limpiar filtros',
                onClick: () => {
                  setSearch('');
                  setParams(new URLSearchParams(), { replace: true });
                },
                testId: 'worklist-empty-clear',
              }}
            />
          )
        ) : (
          <ul className="wl-list" data-testid="worklist-list">
            {filtered.map((work) => {
              const isPinned = pinnedSet.has(work.id);
              return (
              <li key={work.id} className="wl-card-wrap">
                <button
                  type="button"
                  className="wl-card"
                  data-testid={`worklist-card-${work.id}`}
                  onClick={() => navigate(`/works/${work.id}`)}
                >
                  <div className="wl-card-main">
                    <h3 className="wl-card-title">{work.title}</h3>
                    {work.clientName && <p className="wl-card-client">{work.clientName}</p>}
                    {work.clientAddress && <p className="wl-card-address">{work.clientAddress}</p>}
                    {(() => {
                      // Secondary meta row: assigned-technician avatars on the
                      // left, relative `updatedAt` on the right. Either side
                      // can be missing — render only what we have, and skip
                      // the row entirely if both are empty.
                      const assigned = work.assignedTechnicians ?? [];
                      const visible = assigned.slice(0, 2);
                      const overflow = assigned.length - visible.length;
                      const hasUpdated = typeof work.updatedAt === 'number';
                      if (assigned.length === 0 && !hasUpdated) return null;
                      return (
                        <div className="wl-card-meta">
                          {assigned.length > 0 && (
                            <div className="wl-card-avatars" aria-label="Técnicos asignados">
                              {visible.map((id) => {
                                const name = workerById.get(id);
                                return (
                                  <span
                                    key={id}
                                    className="wl-avatar"
                                    title={name ?? `Worker ${id.slice(0, 6)}`}
                                    aria-hidden="true"
                                  >
                                    {name ? initials(name) : '?'}
                                  </span>
                                );
                              })}
                              {overflow > 0 && (
                                <span className="wl-avatar wl-avatar--more" aria-hidden="true">
                                  +{overflow}
                                </span>
                              )}
                            </div>
                          )}
                          {hasUpdated && (
                            <span className="wl-card-updated">{formatRelative(work.updatedAt)}</span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <StatusBadge
                    status={work.status}
                    label={STATUS_LABEL[work.status]}
                    testId={`worklist-card-${work.id}-status`}
                  />
                  <PriorityBadge
                    priority={work.priority}
                    testId={`worklist-card-${work.id}-priority`}
                  />
                  <ChevronRight
                    size={20}
                    strokeWidth={2}
                    className="wl-card-chevron"
                    aria-hidden="true"
                  />
                </button>
                {/* Pin toggle floats over the card. Rendered as a
                    sibling — not a child — of the card button to
                    avoid nested-interactive-control HTML. */}
                <button
                  type="button"
                  className={`wl-card-pin${isPinned ? ' wl-card-pin--active' : ''}`}
                  data-testid={`worklist-card-${work.id}-pin`}
                  aria-label={isPinned ? 'Desfijar' : 'Fijar arriba'}
                  aria-pressed={isPinned}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    togglePinnedWork(work.id);
                  }}
                >
                  <Pin
                    size={16}
                    strokeWidth={2}
                    fill={isPinned ? 'currentColor' : 'none'}
                    aria-hidden="true"
                  />
                </button>
              </li>
              );
            })}
          </ul>
        )}
      </main>
      <button
        type="button"
        className="wl-fab"
        onClick={() => navigate('/works/new')}
        data-testid="worklist-fab-create"
        aria-label="Nuevo trabajo"
      >
        <Plus size={24} strokeWidth={2} aria-hidden="true" />
      </button>
    </div>
  );
}
